import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Sparkles } from "lucide-react";
import { ARTICLES, getArticleBySlug } from "@/blog/articles";
import SEO, { SITE_ORIGIN } from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) return <Navigate to="/blog" replace />;
  const Body = article.body;
  const canonicalPath = `/blog/${article.slug}`;
  const url = `${SITE_ORIGIN}${canonicalPath}`;
  const heroImageUrl = article.hero_image ? `${SITE_ORIGIN}${article.hero_image}` : undefined;

  // Related articles: prefer same pillar/spoke; fill up to 3.
  const related = ARTICLES
    .filter(a => a.slug !== article.slug)
    .sort((a, b) => {
      const scoreOf = (x) => {
        if (article.pillar && x.spoke_of === article.pillar) return 3;
        if (article.spoke_of && x.pillar === article.spoke_of) return 3;
        if (article.spoke_of && x.spoke_of === article.spoke_of) return 2;
        return 1;
      };
      return scoreOf(b) - scoreOf(a);
    })
    .slice(0, 3);

  // Schema: Article + BreadcrumbList always, FAQPage/HowTo when applicable.
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary || article.metaDesc,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "es-CL",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@type": "Organization", name: "borra-me.com", url: SITE_ORIGIN },
      publisher: {
        "@type": "Organization",
        name: "borra-me.com",
        logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/favicon.svg` },
      },
      image: heroImageUrl ? [heroImageUrl] : undefined,
      keywords: article.keywords,
      articleSection: "Privacidad y datos personales Chile",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_ORIGIN}/blog` },
        { "@type": "ListItem", position: 3, name: article.title, item: url },
      ],
    },
  ];

  if (article.faqs && article.faqs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map(f => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (article.steps && article.steps.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: article.title,
      description: article.summary || article.metaDesc,
      inLanguage: "es-CL",
      totalTime: `PT${article.readMin}M`,
      step: article.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <SEO
        title={`${article.metaTitle} | borra-me.com`}
        description={article.metaDesc}
        canonical={canonicalPath}
        keywords={article.keywords}
        ogImage={article.hero_image}
        ogType="article"
        schemas={schemas}
      />

      {/* Article header */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-14">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-8">
            <ArrowLeft className="w-4 h-4" /> Volver al blog
          </Link>
          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-gray-500 mb-5">
            <span>{new Date(article.date).toLocaleDateString("es-CL", { day: "2-digit", month: "long", year: "numeric" })}</span>
            <span className="text-gray-300">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{article.readMin} min lectura</span>
          </div>
          <h1 data-testid="blogpost-h1" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1]">
            {article.title}
          </h1>
          <p className="mt-6 text-lg text-gray-700 leading-relaxed">{article.metaDesc}</p>
        </div>
      </section>

      {/* Hero image */}
      {article.hero_image && (
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pb-10">
            <img
              src={article.hero_image}
              alt={article.hero_alt || article.title}
              width="1024"
              height="576"
              loading="eager"
              fetchpriority="high"
              className="w-full aspect-[16/9] object-cover border border-gray-200"
              data-testid="blogpost-hero-img"
            />
          </div>
        </section>
      )}

      {/* TL;DR / En resumen */}
      {article.summary && (
        <section className="bg-[#F9F9F8] border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
            <div className="border-l-4 border-[#DC2626] bg-white p-6" data-testid="blogpost-tldr">
              <div className="eyebrow text-[#DC2626] mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> En resumen
              </div>
              <p className="text-[15px] leading-relaxed text-gray-800">{article.summary}</p>
            </div>
          </div>
        </section>
      )}

      {/* Article body */}
      <article className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-10" data-testid="blogpost-body">
        <Body />
      </article>

      {/* FAQ section — visible + schema */}
      {article.faqs && article.faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 pb-14" data-testid="blogpost-faq">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mt-6 mb-6">
            Preguntas frecuentes
          </h2>
          <div className="divide-y divide-gray-200 border-y border-gray-200">
            {article.faqs.map((f, i) => (
              <div key={i} className="py-5" data-testid={`blogpost-faq-${i}`}>
                <div className="font-semibold text-gray-900">{f.q}</div>
                <p className="mt-2 text-[15px] leading-relaxed text-gray-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA bottom strip */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="eyebrow text-gray-400 mb-2">borra-me.com</div>
            <h3 className="font-display text-2xl font-bold leading-tight">¿Quieres saber si tus datos están expuestos?</h3>
            <p className="text-gray-300 text-sm mt-2">Escaneo gratis de los principales brokers chilenos en 15 segundos. Sin tarjeta, sin registro.</p>
          </div>
          <Link to="/" data-testid="blogpost-cta"
            className="bg-[#DC2626] hover:bg-red-700 text-white px-6 py-4 font-semibold inline-flex items-center gap-2 whitespace-nowrap">
            Escanear ahora <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Related */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <div className="eyebrow mb-6">Sigue leyendo</div>
        <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
          {related.map(a => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="bg-white p-6 hover:bg-[#F9F9F8] group" data-testid={`related-${a.slug}`}>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-gray-500 mb-3">
                {a.readMin} min lectura
              </div>
              <div className="font-display text-base font-bold text-gray-900 leading-snug group-hover:text-[#0B2447]">
                {a.title}
              </div>
              <p className="mt-3 text-xs text-gray-600 leading-relaxed">{a.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
